import React, { useState, useEffect } from 'react'

import { useNavigate, useParams } from 'react-router-dom'

// Services
import LocalStorageService from '../../services/LocalStorageService'
import NotificationService from '../../services/NotificationService'

// Appt Components
import Anchor from '../../components/simple/anchor/Anchor'
import Box from '../../components/simple/box/Box'
import Button from '../../components/simple/button/Button'
import Form from '../../components/simple/form/Form.js'
import FormField from '../../components/simple/formField/FormField.js'
import ResponsiveGrid from '../../components/compound/responsiveGrid/ResponsiveGrid'
import OptionButtons from '../../components/compound/optionButtons/OptionButtons'
import Text from '../../components/simple/text/Text'
import TextArea from '../../components/simple/input/TextArea'
import TextInput from '../../components/simple/input/TextInput'
import AIService from '../../services/AIService.js'

function PromptEdit () {
  const navigate = useNavigate()

  const [promptDetails, setPromptDetails] = useState({
    name: '',
    addressLineOne: '',
    postcode: '',
    active: '',
    city: '',
    notes: '',
    admin: '',
    label: '',
    existingOrgAdmin: false,
    hasSites: false,
    numAllowedUsers: undefined,
    numUsers: 0
  })

  const [userCount, setUserCount] = useState(0)

  // const { error, showError } = useErrorHandler(null)

  const params = useParams()
  const promptId = params.id
  const promptSection = params.section

  const apiToken = LocalStorageService.get('apiToken')

  // Submit form values
  const submitForm = async values => {
    // if there wasn't an orgadmin when we started this, submit the new org admin
    const promptData = {
      section: values.section,
      subSection: values.subSection,
      label: values.label,
      active: values.active === "Yes" ? true : false,
      isSystemPrompt: values.isSystemPrompt ? true : false,
      prompt: values.prompt,
    };

    if (promptId === 'new') {
      const promptAdded = await AIService.createPrompt(apiToken, promptData);
      if (promptAdded.error) {
        NotificationService.error( 'Error adding the prompt, please try again - ' + promptAdded.error, );
      } else {
        NotificationService.info('Prompt added');
        if (promptAdded?.data?.[0]?.id) {
          navigate(`/prompt/${promptAdded?.data?.[0]?.id}/edit`);
        }
      }
    } else {
      const promptUpdated = await AIService.updatePrompt(apiToken, promptId, promptData );
      if (promptUpdated.error) {
        NotificationService.error( 'Error updating the prompt, please try again - ' + promptUpdated.error,);
      } else {
        NotificationService.info('Prompt updated');
        getPromptDetails(promptId)
      }
    }
  };

  const goBack = () => {
    navigate(`/prompt`)
  }    


  // Get prompt details
  const getPromptDetails = async (promptId) => {

    const promptDetails = await AIService.getFullPrompt(apiToken, promptId)
    if (promptDetails?.error) {
      NotificationService.error(promptDetails.error)
    } else {

      let newPromptData = promptDetails?.prompt || {}


      newPromptData.active = newPromptData?.active ? 'Yes' : 'No'

      setPromptDetails(newPromptData)

    }
  }

  useEffect(() => {
    if (promptId !== 'new') {
      getPromptDetails(promptId)
    } else {
      setPromptDetails({
        section: promptSection || 'Student',
        subSection: '',
        label: '',
        isSystemPrompt: false,
        active: 'No',
        prompt: ''
      })
    }

  }, [])



  return (
    <Box width="xlarge">
      <Box
        background="white"
        direction="column"
        gap="small"
        margin={{ bottom: 'medium' }}
        round="small"
      >
        <Box direction="column" pad={{ horizontal: 'medium' }}>
          <Text margin={{ top: 'small' }} size="xlarge">
            Prompts:{' '}
            {promptDetails.id
              ? promptDetails.name
              : 'New Prompt'}
          </Text>
          <Text margin={{ bottom: 'small', top: 'xsmall' }} size="xsmall">
            <Anchor href="/">Behaviour Smart</Anchor>
            <Text color="brand" size="xsmall">
              {location.pathname}
            </Text>
          </Text>
        </Box>
      </Box>

      <Box background="white" direction="column" gap="small" round="small">
        <Box
          gap="small"
          margin={{ horizontal: 'small' }}
          pad="small"
          round="small"
        >
          <Box direction="column" gap="medium">

            <Text size="xlarge">Prompt Details</Text>

            <Form
              //   validate='blur'
              onChange={nextValue => {
                // console.log(nextValue)
                setPromptDetails(nextValue);
              }}
              onSubmit={({ value: nextValue }) => {
                // console.log(nextValue)
                submitForm(nextValue);
              }}
              value={promptDetails}
            >
              <ResponsiveGrid
                rows={['auto']}
                columns={{
                  small: ['auto'],
                  medium: ['auto', 'auto'],
                  large: ['auto', 'auto'],
                  xlarge: ['auto', 'auto'],
                }}
                gap="small"
              >
                <Box direction="column">

                  {!promptDetails?.isSystemPrompt && <FormField
                    direction="column"
                    label="Button Label"
                    name="label"
                  >
                    <TextInput name="label" type="text" />
                  </FormField>}

                  <FormField
                    direction="column"
                    label="Prompt"
                    name="prompt"
                  >
                    <TextArea fill={true} name="prompt" type="text" rows={15} data-min-rows='15' />
                  </FormField>
                </Box>

                <Box direction="column" alignItems="flex-start" margin={{ left: 'small' }}>

                  {!promptDetails?.isSystemPrompt && <Box direction="row-responsive" gap="medium" margin={{ top: 'medium' }}>
                    <FormField direction="column" label="Active" name="active">
                      <OptionButtons
                        id="active"
                        name="active"
                        // value='Yes'
                      />
                    </FormField>
                  </Box>}
      
                  {!promptDetails?.isSystemPrompt && <Box direction="row-responsive" gap="medium">
                    <FormField direction="column" label="Section" name="section">
                      <OptionButtons
                        id="section"
                        name="section"
                        options={['Student', 'Incident', 'Service']}
                      />
                    </FormField>
                  </Box>}



                  {!promptDetails?.isSystemPrompt && <FormField direction="column" label="Sub Section" name="subSection">
                    <TextInput name="subSection" type="text" />
                  </FormField>}


                  

                  {promptDetails?.isSystemPrompt && <>
                    <Box direction="row-responsive" gap="medium" margin={{ top: 'medium' }}>
                      <FormField direction="column" label="Type" name="active">
                        <Text weight="bold" size="large">System Prompt</Text>
                      </FormField>
                    </Box>

                    <Box direction="row-responsive" gap="medium" margin={{ top: 'medium' }}>
                      <FormField direction="column" label="Section" name="active">
                        <Text weight="bold" size="large">{promptDetails.section}</Text>
                      </FormField>
                    </Box>
                  </>}
                </Box>
              </ResponsiveGrid>

              <Box direction="row" justify="between" margin={{ top: 'medium' }}>
                <Button label="< Back" onClick={() => goBack()} secondary />
                <Button type="submit" label="Save" primary />
              </Box>
            </Form>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default PromptEdit
